import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ClipboardList, Store, FileBarChart, CalendarCog, Flower2, Bell, LayoutTemplate, AlertTriangle, Repeat, Tag, PanelTop, Users as UsersIcon } from "lucide-react";
import { adminApi } from "@/lib/api";

const CARDS = [
  { to: "/admin/orders", label: "Live Orders", desc: "View & manage all store orders", icon: ClipboardList, color: "#28282E" },
  { to: "/admin/missing-orders", label: "Missing Orders", desc: "Chase stores that haven't ordered", icon: AlertTriangle, color: "#D46117" },
  { to: "/admin/standing-orders", label: "Standing Orders", desc: "Preview & run weekly auto-orders", icon: Repeat, color: "#28282E" },
  { to: "/admin/specials", label: "Friday Specials", desc: "Create & expire weekly specials", icon: Tag, color: "#D46117" },
  { to: "/admin/stores", label: "Store Accounts", desc: "Manage IGA stores & holds", icon: Store, color: "#D46117" },
  { to: "/admin/production", label: "Production Report", desc: "Compile & email Metcash", icon: FileBarChart, color: "#28282E" },
  { to: "/admin/store-orders", label: "Store Orders Report", desc: "Per-store breakdown for distribution", icon: Store, color: "#D46117" },
  { to: "/admin/schedule", label: "Delivery Schedule", desc: "Cutoffs & special events", icon: CalendarCog, color: "#D46117" },
  { to: "/admin/home-content", label: "Home Content", desc: "Edit store home tiles & images", icon: LayoutTemplate, color: "#D46117" },
  { to: "/admin/landing-page", label: "Landing Page", desc: "Edit store login title, subtitle & hero", icon: PanelTop, color: "#28282E" },
  { to: "/admin/users", label: "Internal Users", desc: "Invite team members & set access levels", icon: UsersIcon, color: "#D46117", area: "users" },
];
const AREA = { "/admin/orders": "orders", "/admin/missing-orders": "orders", "/admin/standing-orders": "orders", "/admin/specials": "catalogue", "/admin/stores": "stores", "/admin/production": "reports", "/admin/store-orders": "reports", "/admin/schedule": "settings", "/admin/home-content": "catalogue", "/admin/landing-page": "catalogue", "/admin/users": "users" };

export default function Hub() {
  const { can } = useAuth();
  const [stats, setStats] = useState({ stores: 0, orders: 0, flowers: 0, pending: 0 });
  useEffect(() => {
    Promise.all([
      adminApi.stores().catch(() => []),
      adminApi.allOrders("all").catch(() => []),
      adminApi.flowers().catch(() => []),
      adminApi.pendingReviews().catch(() => ({ pending_review_count: 0 })),
    ]).then(([s, o, f, p]) => setStats({ stores: s.length, orders: o.length, flowers: f.length, pending: p.pending_review_count || 0 }));
  }, []);

  return (
    <div data-testid="admin-hub">
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-bold text-[#28282E]">Hasfarm Admin Hub</h1>
        <p className="text-sm text-[#28282E]/70">Everything you need to run Metcash fulfilment.</p>
      </div>
      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[["Stores", stats.stores, Store], ["Orders", stats.orders, ClipboardList], ["Flowers", stats.flowers, Flower2], ["Needs review", stats.pending, Bell]].map(([l, v, I]) => (
          <div key={l} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wide text-[#28282E]/55">{l}</span><span className="grid h-9 w-9 place-items-center rounded-lg bg-[#FBEEF4] text-[#28282E]"><I className="h-4 w-4" /></span></div>
            <div className="mt-3 text-3xl font-bold text-[#28282E]">{v}</div>
          </div>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {CARDS.filter((c) => can(AREA[c.to] || "catalogue")).map((c) => (
          <Link key={c.to} to={c.to} data-testid={`hub-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-lg">
            <span className="grid h-12 w-12 place-items-center rounded-2xl text-white" style={{ backgroundColor: c.color }}><c.icon className="h-6 w-6" /></span>
            <div><div className="font-serif text-lg font-semibold text-[#28282E]">{c.label}</div><div className="text-sm text-[#28282E]/70">{c.desc}</div></div>
          </Link>
        ))}
      </div>
    </div>
  );
}
